import { Injectable } from '@jack-henry/frontend-utils/di';
import { AchExceptionDetailDataModelDto, DashboardClient } from '@treasury/api/channel';
import { PayOrReturnChoice } from '../../mappings/ach';
import { PositivePayExceptionRequests } from '../../requests/positive-pay/positive-pay-exception-requests.js';
import { AchExceptionDetail } from './entities/ach-exception-detail';

type CustomRecord = {
    decisionChoice: string;
    decisionReviewLabel: string;
    achExceptionDecisionStatusId: number;
};

@Injectable()
export class AchExceptionsService {
    constructor(private readonly dashboardClient: DashboardClient) {}

    /**
     * This method is used to save ACH exceptions.
     * It makes a call to the positive pay exception requests to save the exceptions.
     * @param {CustomRecord[]} records - The list of ACH exceptions.
     * @returns {Promise<void>} - The list of ACH exceptions.
     * @deprecated Please create an instance method instead of using this static method
     */
    public static async saveAchExceptions(records: CustomRecord[]) {
        const exceptions = records.map(record => ({
            ...record,
            decisionReviewLabel: record.decisionChoice,
            achExceptionDecisionStatusId:
                record.decisionChoice in PayOrReturnChoice
                    ? PayOrReturnChoice[record.decisionChoice as keyof typeof PayOrReturnChoice]
                    : PayOrReturnChoice.Unselected,
        }));
        return PositivePayExceptionRequests.saveAchExceptionsDashboard(exceptions);
    }

    /**
     * This method is used to get ACH exceptions for a dashboard widget.
     * It makes a call to the dashboard client to fetch the exceptions.
     * The exceptions are then mapped to the AchExceptionDetail model.
     * @returns {Promise<AchExceptionDetail[]>} - The list of ACH exceptions.
     */
    public async getAchExceptionsForWidget() {
        const response = await this.dashboardClient.dashboardGetWorkExceptions({
            type: 'achExceptions',
        });
        /*
         * Note: The API response returns various types of exceptions based on the requested type.
         * We are only interested in the ACH exceptions, there for we specify the type in the request
         * and cast to the correct AchExceptionDetailDataModelDto type and map from there.
         */
        const achExceptions: AchExceptionDetailDataModelDto[] = response.data || [];
        return achExceptions.map(achException => new AchExceptionDetail(achException));
    }
}
